import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, DatePicker, Checkbox } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import axios, { AxiosError, AxiosResponse } from "axios";
import TextArea from "antd/lib/input/TextArea";
import { Moment } from "moment";
import { IProject, IProjectType } from "..";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { removeEmptyKeys } from "../../../utils/formatters";
import { IUser } from "../../Users";
import ProjectTypes from "../../../enums/ProjectTypes";
import { ICustomer } from "../../Customers";
import filterOptions from "../../../utils/filterOptions";
import { IContractor } from "../../Contractors";
import ITypePricingProject from "../../../interfaces/ITypePricingProject";

const { RangePicker } = DatePicker;

interface IProjectModalProps {
  visible: boolean;
  handleCloseModal: (createdProject?: IProject) => void;
}

interface IProjectFormData {
  project_type_id: string;
  manager_id: string;
  identifier: string;
  customer_id: string;
  base_project_id: string;
  short_description: string;
  full_description: string;
  contractor_name: string;
  contractor_email: string;
  contractor_phone_number: string;
  pricing: string;
  public_project: boolean;
  expected_dates: {
    0: Moment;
    1: Moment;
  };
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const ProjectModal: React.FC<IProjectModalProps> = ({
  visible,
  handleCloseModal,
}) => {
  const { t } = useTranslation(["projects"]);
  const [loading, setLoading] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProjectTypes, setLoadingProjectTypes] = useState(false);
  const [loadingConcludedProjects, setLoadingConcludedProjects] = useState(
    false,
  );
  const [checkPublic, setCheckPublic] = useState(false);
  const [contractorName, setContractorName] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [managers, setManagers] = useState<IUser[]>([]);
  const [concludedProjects, setConcludedProjects] = useState<IProject[]>([]);
  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [projectTypes, setProjectTypes] = useState<IProjectType[]>([]);
  const [selectedType, setSelectedType] = useState<ProjectTypes | undefined>(
    undefined,
  );
  const [contractors, setContractors] = useState<IContractor[]>([]);
  const [selectPricing, setSelectPricing] = useState(
    ITypePricingProject.HoursFactory,
  );

  const { addToast } = useToast();
  const [form] = Form.useForm();

  useEffect(() => {
    setLoadingManagers(true);
    api
      .get<IUser[]>("/users", {
        params: {
          role: "MANAGER",
        },
      })
      .then(response => {
        setManagers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingManagers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showBaseProject = useMemo(
    () =>
      !!selectedType &&
      (selectedType === ProjectTypes.Corrective ||
        selectedType === ProjectTypes.Incident),
    [selectedType],
  );

  const internalProject = useMemo(
    () => !!selectedType && selectedType === ProjectTypes.Internal,
    [selectedType],
  );

  useEffect(() => {
    setLoadingConcludedProjects(true);
    api
      .get<IProject[]>("/projects", {
        params: removeEmptyKeys({
          status: !showBaseProject ? "CONCLUDED" : undefined,
        }),
      })
      .then(response => {
        setConcludedProjects(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os projetos concluídos, tente novamente.",
        });
      })
      .finally(() => setLoadingConcludedProjects(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBaseProject]);

  useEffect(() => {
    setLoadingCustomers(true);
    api
      .get<ICustomer[]>("/customers")
      .then(response => {
        setCustomers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os clientes, tente novamente.",
        });
      })
      .finally(() => setLoadingCustomers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoadingProjectTypes(true);
    api
      .get<IProjectType[]>("/project-types")
      .then(response => {
        setProjectTypes(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingProjectTypes(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const managerOptions = useMemo(() => {
    if (!managers) {
      return undefined;
    }

    return managers.map(manager => ({
      label: manager.name,
      value: manager.id,
    }));
  }, [managers]);

  const customersOptions = useMemo(() => {
    if (!customers) {
      return undefined;
    }

    return customers.map(customer => ({
      label: customer.name,
      value: customer.id,
    }));
  }, [customers]);

  const projectTypesOptions = useMemo(() => {
    if (!projectTypes) {
      return undefined;
    }

    return projectTypes.map(projectType => ({
      label: t(`project-types.options.${projectType.acronym}.label`),
      value: projectType.id,
    }));
  }, [projectTypes, t]);

  const concludedProjectsOptions = useMemo(() => {
    if (!concludedProjects) {
      return undefined;
    }

    return concludedProjects.map((project: IProject) => ({
      label: project.identifier
        ? `${project.identifier} - ${project.short_description}`
        : project.short_description,
      value: project.id,
    }));
  }, [concludedProjects]);

  const optionsSeniority = useMemo(() => {
    return [
      {
        label: "Valor de Hora Fabrica",
        value: ITypePricingProject.HoursFactory,
      },
      {
        label: "Valor por senioridade",
        value: ITypePricingProject.HoursSeniority,
      },
    ];
  }, [ITypePricingProject]);

  const handleClose = useCallback(
    (createdProject?: IProject) => {
      form.resetFields();
      handleCloseModal(createdProject);
      setValidationErrors({});
    },
    [form, handleCloseModal],
  );

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description = "Ocorreu um erro ao criar o projeto, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro na criação",
        description,
      });
    },
    [addToast],
  );

  const showIdentifier = useMemo(
    () => !!selectedType && selectedType !== ProjectTypes.Internal,
    [selectedType],
  );

  const showSeniority = useMemo(
    () => !!selectedType && selectedType === ProjectTypes.Factory,
    [selectedType],
  );

  const handleSubmit = useCallback(
    async (dataForm: IProjectFormData) => {
      const data = removeEmptyKeys({
        ...dataForm,
        contractor_name: contractorName,
        public_project: internalProject ? checkPublic : false,
      });

      try {
        const schema = Yup.object().shape({
          public_project: Yup.boolean(),
          project_type_id: Yup.string().required("Tipo de projeto obrigatório"),
          manager_id: Yup.string().required("Manager obrigatório"),
          identifier: Yup.string().nullable(),
          customer_id: Yup.string()
            .nullable()
            .when("project_type_id", (_, field) => {
              return showIdentifier
                ? field.required("Cliente obrigatório")
                : field;
            }),
          base_project_id: Yup.string()
            .nullable()
            .when("project_type_id", (_, field) => {
              return showBaseProject
                ? field.required("Projeto base obrigatório")
                : field;
            }),
          short_description: Yup.string().required(
            "Descrição resumida obrigatória",
          ),
          full_description: Yup.string().required(
            "Descrição detalhada obrigatória",
          ),
          contractor_name: Yup.string().when("project_type_id", (_, field) => {
            return showIdentifier
              ? field.required("Nome do contratante obrigatório")
              : field;
          }),
          contractor_email: Yup.string()
            .email("Digite um e-mail válido")
            .when("project_type_id", (_, field) => {
              return showIdentifier
                ? field.required("Email do contratante obrigatório")
                : field;
            }),
          contractor_phone_number: Yup.string(),
          expected_dates: Yup.array().when("project_type_id", (_, field) => {
            return selectedType === ProjectTypes.Allocation
              ? field.required("Datas estimadas obrigatórias")
              : field;
          }),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const {
          project_type_id,
          manager_id,
          identifier,
          customer_id,
          base_project_id,
          short_description,
          full_description,
          contractor_name,
          contractor_email,
          contractor_phone_number,
          expected_dates,
          public_project,
        } = data;

        const payload = {
          project_type_id,
          pricing: selectedType === ProjectTypes.Factory ? selectPricing : null,
          manager_id,
          identifier,
          customer_id,
          base_project_id,
          short_description,
          full_description,
          contractor_name,
          contractor_email,
          contractor_phone_number,
          public_project,
          expected_start:
            expected_dates &&
            expected_dates[0]
              .hour(0)
              .subtract(3, "hours")
              .minute(0)
              .second(0)
              .millisecond(0)
              .toISOString(),
          expected_end:
            expected_dates &&
            expected_dates[1]
              .hour(0)
              .subtract(3, "hours")
              .minute(0)
              .second(0)
              .millisecond(0)
              .toISOString(),
        };

        const response = await api.post("/projects", payload);

        addToast({
          type: "success",
          title: "Projeto criado",
          description: "O projeto foi criado com sucesso",
        });

        handleClose(response.data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      generateErrorToast,
      handleClose,
      showBaseProject,
      showIdentifier,
      contractorName,
      selectedType,
      selectPricing,
      checkPublic,
      internalProject,
    ],
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      setSelectedType(projectTypes.find(type => type.id === value)?.acronym);
      setValidationErrors({});
    },
    [projectTypes],
  );

  const contractorOptions = useMemo(() => {
    if (!contractors) {
      return undefined;
    }

    return contractors
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(ctt => ({
        label: ctt.name,
        value: `${ctt.name},${ctt.id}`,
      }));
  }, [contractors]);

  const selectCustomer = useCallback(
    (input: string) => {
      const selectedCustomer = customers.find(manager => manager.id === input);
      setContractors(selectedCustomer?.contractors || []);
      setContractorName("");
      form.setFieldsValue({
        contractor_phone_number: "",
        contractor_email: "",
        contractor_name: "",
      });
    },
    [form, customers],
  );

  const selectContractor = useCallback(
    (input: string) => {
      const [name, id] = input.split(",");
      setContractorName(name);
      const selectedContractor = contractors.find(manager => manager.id === id);

      form.setFieldsValue({
        contractor_phone_number: selectedContractor?.phone_number || "",
        contractor_email: selectedContractor?.email || "",
      });
    },
    [form, contractors],
  );

  const handleSeniority = useCallback((value: ITypePricingProject) => {
    setSelectPricing(value);
  }, []);

  return (
    <Modal
      title={t("actions.options.create")}
      visible={visible}
      width={800}
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem
          name="project_type_id"
          required
          label={t("project-types.label")}
          errors={validationErrors}
        >
          <Select
            allowClear
            onChange={handleTypeChange}
            options={projectTypesOptions}
            loading={loadingProjectTypes}
          />
        </FormItem>
        {showSeniority ? (
          <>
            <FormItem name="pricing" label="Precificação">
              <Select
                options={optionsSeniority}
                onChange={handleSeniority}
                defaultValue={ITypePricingProject.HoursFactory}
              />
            </FormItem>
          </>
        ) : (
          <></>
        )}
        {showBaseProject && (
          <FormItem
            name="base_project_id"
            required={showBaseProject}
            label={t("base_project")}
            errors={validationErrors}
          >
            <Select
              showSearch
              allowClear
              filterOption={filterOptions}
              options={concludedProjectsOptions}
              loading={loadingConcludedProjects}
              placeholder="Pesquise um projeto base"
            />
          </FormItem>
        )}
        {showIdentifier && (
          <FormItem
            name="identifier"
            // required={showIdentifier}
            label={t("identifier")}
            errors={validationErrors}
          >
            <Input />
          </FormItem>
        )}
        <FormItem
          name="manager_id"
          required
          label={t("manager")}
          errors={validationErrors}
        >
          <Select
            allowClear
            showSearch
            filterOption={filterOptions}
            options={managerOptions}
            loading={loadingManagers}
          />
        </FormItem>
        <FormItem
          name="expected_dates"
          label={t("expected_dates")}
          errors={validationErrors}
          required={selectedType === ProjectTypes.Allocation}
        >
          <RangePicker
            style={{ width: "100%" }}
            format={["DD/MM/YYYY", "DD/MM/YYYY"]}
            placeholder={[t("expected_start"), t("expected_end")]}
          />
        </FormItem>
        <FormItem
          name="short_description"
          required
          label={t("short_description")}
          errors={validationErrors}
        >
          <Input maxLength={100} showCount />
        </FormItem>
        <FormItem
          name="full_description"
          required
          label={t("full_description")}
          errors={validationErrors}
        >
          <TextArea rows={6} spellCheck={false} />
        </FormItem>
        {showIdentifier && (
          <>
            <FormItem
              name="customer_id"
              required={showIdentifier}
              label={t("customer")}
              errors={validationErrors}
            >
              <Select
                allowClear
                showSearch
                filterOption={filterOptions}
                options={customersOptions}
                onChange={selectCustomer}
                loading={loadingCustomers}
              />
            </FormItem>
            <FormItem
              name="contractor_name"
              required={showIdentifier}
              label={t("contractor_name")}
              errors={validationErrors}
            >
              <Select
                allowClear
                showSearch
                disabled={!contractors.length}
                filterOption={filterOptions}
                options={contractorOptions}
                onChange={selectContractor}
              />
            </FormItem>
            <FormItem
              name="contractor_email"
              required={showIdentifier}
              label={t("contractor_email")}
              errors={validationErrors}
            >
              <Input disabled={!contractorName} />
            </FormItem>
            <FormItem
              name="contractor_phone_number"
              label={t("contractor_phone_number")}
              errors={validationErrors}
            >
              <Input disabled={!contractorName} />
            </FormItem>
          </>
        )}
        {internalProject ? (
          <FormItem label={t("Tornar o projeto livre para todos?")}>
            <Checkbox
              name="public_project"
              defaultChecked={false}
              onChange={() => setCheckPublic(old => !old)}
            />
          </FormItem>
        ) : null}
      </Form>
    </Modal>
  );
};

export default ProjectModal;
